import {useCallback, useMemo, useSyncExternalStore} from 'react'
import {AGENT_TASKS_SELECTED_MODEL_KEY} from '../utils/constants'

function getStorageKey(vendors?: string[]): string {
  if (!vendors || vendors.length === 0) return AGENT_TASKS_SELECTED_MODEL_KEY
  return `${AGENT_TASKS_SELECTED_MODEL_KEY}:${agentModelScopeKey(vendors)}`
}

/**
 * Stable, normalized key for the agent/vendor scope a model selection is stored under.
 * Empty string for the default (no-vendor) scope. Matches the normalization used for
 * the per-vendor localStorage key so scope-aware consumers stay in sync.
 */
export function agentModelScopeKey(vendors?: string[]): string {
  if (!vendors || vendors.length === 0) return ''
  return vendors
    .map(v => v.toLowerCase())
    .sort()
    .join(',')
}

/**
 * Hook for managing the latest selected agent model as a plain string in localStorage.
 * Uses useSyncExternalStore to properly subscribe to localStorage changes across tabs.
 *
 * When `vendors` is provided, the model selection is stored per-vendor-set so that
 * switching between vendors (e.g., Copilot → Anthropic → OpenAI) preserves each
 * vendor-set's last-used model independently.
 */
export function useLatestAgentModel(vendors?: string[]): [string | undefined, (value: string | undefined) => void] {
  const storageKey = useMemo(() => getStorageKey(vendors), [vendors])

  const subscribe = useCallback(
    (callback: () => void) => {
      const handleStorageChange = (e: StorageEvent) => {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.key === storageKey) {
          callback()
        }
      }
      window.addEventListener('storage', handleStorageChange)
      return () => window.removeEventListener('storage', handleStorageChange)
    },
    [storageKey],
  )

  const getSnapshot = useCallback(() => {
    try {
      return localStorage.getItem(storageKey) ?? undefined
    } catch {
      return undefined
    }
  }, [storageKey])

  const getServerSnapshot = useCallback(() => undefined, [])

  const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  const setValue = useCallback(
    (newValue: string | undefined) => {
      try {
        const oldValue = localStorage.getItem(storageKey)
        if (newValue === undefined) {
          localStorage.removeItem(storageKey)
        } else {
          localStorage.setItem(storageKey, newValue)
        }
        // Dispatch a storage event for same-window updates
        window.dispatchEvent(
          new StorageEvent('storage', {
            key: storageKey,
            newValue: newValue ?? null,
            oldValue,
          }),
        )
      } catch {
        // localStorage not available
      }
    },
    [storageKey],
  )

  return [value, setValue]
}
